import { useRef, useEffect, useState } from 'react';
import RichText from 'components/shared/richtext';
import Button from 'components/shared/button';
import useIsMobile from 'hooks/utils/useIsMobile';
import BuyingPros from 'components/shared/buyingPros';
import Breadcrumbs from 'components/layout/breadcrumbs/breadcrumbs';
import WishlistButton from 'components/shared/wishlist-button';
import classNames from 'classnames';
import UnitPrice from 'components/shared/unit-price/unitPrice';
import AttributeTag from '../attributeTag/attributeTag';
import CartSection from '../cartSection/cartSection';
import styles from './productInfo.module.scss';
import HoldAndShine from '../holdAndShine/holdAndShine';
import ProductVariations from '../productVariations';
import Subscription from '../subscription/subscription';

const ProductInfo = ({ locale, productData, resources }) => {
  const {
    title,
    breadcrumbs,
    description,
    productAttributes,
    productTags,
    linkToExternalWebshop,
    holdFactor,
    shineFactor,
    shopifyData,
    slug,
    size,
  } = productData;

  const isMobile = useIsMobile();
  const cartSectionRef = useRef(null);

  const { buyingProsSection, outOfStock: outOfStockMessage } = resources.productpage;
  const [scrolledDown, setScrolledDown] = useState(false);
  const [sellingPlan, setSellingPlan] = useState(null);
  const lastScrollTop = useRef(0);

  const {
    onlyAvailableAtSalon,
    findASalonButton,
    dontShipToDestination,
    contactUsButton,
    shineFactorTitle,
    holdFactorTitle,
  } = resources.secondpartproductpage;

  const { localesWithWebshop, localesWithSalonFinder, saleText } = resources.settings;

  const outOfStock = shopifyData?.outOfStock;
  const productPrice = shopifyData?.productPrice;
  const productCompareAtPrice = shopifyData?.productCompareAtPrice;
  const localeHasWebshop = localesWithWebshop.onOrOff;
  const localeHasSalonFinder = localesWithSalonFinder.onOrOff;
  const salePercentage = (100 - (productPrice / productCompareAtPrice) * 100).toFixed(0);
  const productHasLinkToAnotherProduct =
    productData?.linkToAnotherProduct?.slug && productData?.useLinkToAnotherProduct !== false;

  useEffect(() => {
    document.body.style.paddingBottom = isMobile && localeHasWebshop ? '6rem' : '';
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [isMobile, localeHasWebshop]);

  // detect if the user is scrolling down
  // needed to show/hide the add to cart button on mobile
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      if (st > lastScrollTop.current && lastScrollTop.current > 0) {
        setScrolledDown(true);
      }
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setScrolledDown(false);
    lastScrollTop.current = 0;
  }, [slug]);

  return (
    <div>
      {!isMobile && breadcrumbs && (
        <Breadcrumbs locale={locale} className={styles.productBreadcrumbs} data={breadcrumbs} />
      )}
      <div className={styles.productTagsRow}>
        {productTags?.map(tag => (
          <div className={styles.productTag}>{tag}</div>
        ))}
      </div>
      {localeHasWebshop && !outOfStock && (
        <div className={styles.productTagsRow}>
          {productCompareAtPrice && (
            <div className={styles.salesTag}>{`${saleText?.text || ''} ${salePercentage} %`}</div>
          )}
          {productTags?.map(tag => (
            <div key={`Product tag ${tag}`} className={styles.productTag}>
              {tag}
            </div>
          ))}
        </div>
      )}
      {localeHasWebshop && outOfStock && (
        <div className={styles.productTagsRow}>
          <div className={styles.outOfStockTag}>{outOfStockMessage.text}</div>
        </div>
      )}
      <div className={styles.productHeader}>
        <h1 className={styles.productName}>{title}</h1>
        {localeHasWebshop && (
          <WishlistButton
            className={styles.wishlistIcon}
            productData={productData}
            shopifyData={productData.shopifyData}
          />
        )}
      </div>
      {isMobile ? (
        <>
          <ProductVariations
            locale={locale}
            productData={productData}
            resources={resources}
            localeHasWebshop={localeHasWebshop}
          />
          <UnitPrice
            unitPrice={shopifyData.unitPrice}
            price={shopifyData.productPrice}
            locale={locale}
            className={styles.unitPrice}
            globalModules={resources}
            size={size}
          />
        </>
      ) : null}
      <div className="wae-rating" />

      {isMobile && (
        <Subscription
          shopifyData={shopifyData}
          sellingPlan={sellingPlan}
          setSellingPlan={setSellingPlan}
          resources={resources}
        />
      )}

      <RichText className={styles.productDescription} text={description?.json} />

      {productAttributes && (
        <div className={styles.productAttributes}>
          {productAttributes.map(attribute => (
            <AttributeTag className={styles.attributeTag} key={attribute} attribute={attribute} />
          ))}
        </div>
      )}

      <HoldAndShine
        holdFactorTitle={holdFactorTitle}
        holdFactor={holdFactor}
        shineFactorTitle={shineFactorTitle}
        shineFactor={shineFactor}
      />

      {!isMobile ? (
        <>
          <ProductVariations
            locale={locale}
            productData={productData}
            resources={resources}
            localeHasWebshop={localeHasWebshop}
          />
          <UnitPrice
            unitPrice={shopifyData.unitPrice}
            price={shopifyData.productPrice}
            locale={locale}
            className={styles.unitPrice}
            globalModules={resources}
            size={size}
          />
        </>
      ) : null}

      {!isMobile && (
        <Subscription
          shopifyData={shopifyData}
          sellingPlan={sellingPlan}
          setSellingPlan={setSellingPlan}
          resources={resources}
        />
      )}

      <div ref={cartSectionRef}>
        {((localeHasWebshop && productPrice) || productHasLinkToAnotherProduct) && (
          <div
            className={classNames({
              [styles.fixedCartSectionWrapper]: isMobile,
              [styles.fixedCartSectionWrapperVisible]: isMobile && scrolledDown,
            })}
          >
            <CartSection
              locale={locale}
              resources={resources}
              className={isMobile && styles.fixedCartSection}
              productData={productData}
              shopifyData={shopifyData}
              sellingPlan={sellingPlan}
            />
          </div>
        )}
        {localeHasSalonFinder &&
          !localeHasWebshop &&
          !linkToExternalWebshop?.externalLink &&
          !productHasLinkToAnotherProduct && (
            <>
              <p className={styles.onlyAvailableAtSalonText}>{onlyAvailableAtSalon.text}</p>
              <Button
                locale={locale}
                className={styles.findASalonButton}
                button={findASalonButton.button}
              />
            </>
          )}
        {!localeHasSalonFinder && !localeHasWebshop && !linkToExternalWebshop?.externalLink && (
          <>
            <p className={styles.dontShipToDestinationText}>{dontShipToDestination.text}</p>
            <Button
              locale={locale}
              className={styles.contactUsButton}
              button={contactUsButton.button}
            />
          </>
        )}
        {!localeHasWebshop && linkToExternalWebshop?.externalLink && (
          <>
            <p className={styles.dontShipToDestinationText}>{dontShipToDestination.text}</p>
            <Button
              locale={locale}
              className={styles.findASalonButton}
              button={linkToExternalWebshop}
            />
          </>
        )}
      </div>

      {buyingProsSection?.sections[0] && <BuyingPros data={buyingProsSection.sections[0]} />}
    </div>
  );
};

export default ProductInfo;
